import api from "./api";
import { numbersOnly } from "helpers/utils";
import { User } from "models/users/type_user";
import {
  getMyAccess,
  mergeUserWithAccess,
  persistUserSession,
} from "./access";

export interface IUserLogin extends User {
  message?: string;
}

function getLoginErrorMessage(status?: number, message?: string) {
  if (message) return message;

  if (status === 400) return "Informe CPF e senha para acessar.";
  if (status === 401) return "CPF ou senha incorretos.";
  if (status === 403)
    return "Seu usuario nao tem permissao para acessar o painel.";
  if (status && status >= 500) {
    return "Servidor temporariamente indisponivel. Tente novamente em alguns instantes.";
  }

  return "Nao foi possivel realizar o login. Verifique os dados e tente novamente.";
}

interface ResponseCheckToken {
  expiredToken: boolean;
}

function emptyUser(message: string): IUserLogin {
  return {
    id: "",
    avatar: "",
    nome: "",
    email: "",
    roles: [],
    permissions: [],
    isSuperAdmin: false,
    canAccessPanel: false,
    access_token: "",
    message,
  };
}

/**
 * Fazer o login do usuário no painel administrativo
 */
export async function login(
  cpf: string,
  password: string
): Promise<IUserLogin> {
  try {
    const response = await api.post("/users/admin/login", {
      cpf: numbersOnly(cpf),
      password,
    });

    const payload = response.data as IUserLogin;

    if (response.status >= 400 || !payload.access_token) {
      return {
        ...emptyUser(getLoginErrorMessage(response.status, payload?.message)),
        ...payload,
        access_token: "",
      };
    }

    persistUserSession(payload);

    const access = await getMyAccess();
    const user = mergeUserWithAccess(payload, access);
    persistUserSession(user);

    return user;
  } catch (error: any) {
    return emptyUser(
      getLoginErrorMessage(
        error?.response?.status,
        error?.response?.data?.message
      )
    );
  }
}

/**
 * Listar todos os usuários do sistema
 */
export async function getListUsers() {
  try {
    const users = await api.get("/users");
    return users.data;
  } catch (error) {
    throw new Error("Erro comunicação com o servidor");
  }
}

/**
 * Alterar a senha do usuário
 */
export async function changerPasswordUser(passOld: string, passNew: string) {
  try {
    const userChange = await api.post("/users/change-password", {
      passOld,
      passNew,
    });
    return { status: userChange.status, data: userChange.data };
  } catch (error: any) {
    throw new Error(error.message);
  }
}

/**
 * Checar validade do token
 */
export async function checkAccessToken(): Promise<ResponseCheckToken> {
  try {
    const response = await api.get("/users/check-token");
    const data = response?.data;
    if (data && typeof data.expiredToken === "boolean") {
      return data;
    }
    return { expiredToken: false };
  } catch {
    return { expiredToken: true };
  }
}

/**
 * Upload do avatar do usuário
 */
export async function uploadAvatarUser(userId: string, formData: FormData) {
  return await api.post("/users/upload-avatar", formData, {
    headers: { userid: userId },
  });
}

/**
 * Remover o avatar
 */
export async function deleteAvatarUser(userId: string, filename: string) {
  try {
    const { data, status } = await api.delete(
      `/users/avatar?userid=${userId}&filename=${filename}`
    );
    return { data, status };
  } catch (error: any) {
    return error.message || "Erro comunicação com o servidor";
  }
}

export async function resetPasswordUser(
  userId: string
): Promise<{ message: string }> {
  try {
    const { data } = await api.get("/users/reset-password", {
      headers: { user: userId },
    });

    return data;
  } catch (error: any) {
    return error.message;
  }
}

export async function resendVerifyEmail(
  userId: string
): Promise<{ message: string }> {
  try {
    const { data } = await api.get("/users/resend-verify-email", {
      headers: { user: userId },
    });
    return data;
  } catch (error: any) {
    return error.message;
  }
}
